import type { TFunction } from 'i18next'

import {
  AppAssistantActionType,
  AppAssistantQuickActionId,
  ASSISTANT_TAIL_SPEAKER_LABELS,
  AssistantSpeakerRole,
  getAssistantQuickActions,
  isTargetedQuickActionId,
  type AppAssistantAction,
  type AppAssistantQuickAction,
  type AppAssistantRequest,
  type AppAssistantResult
} from '../../../../../pure-js/types/AppAssistant'
import {
  AssistantActionLabelKey,
  AssistantMessageKey,
  AssistantMessageSource,
  type AssistantLabel,
  type AssistantMessage
} from '../../../../../pure-js/libs/assistantMessages'
import { isFacilitatorPathname } from '../../../../../pure-js/libs/assistantNavigation'
import { meaningfulAriaTranscriptTurns, type AriaTranscriptTurn } from '../../../../../pure-js/libs/ariaTranscript'
import { SAVE_RETRY_BACKOFF_FACTOR, SAVE_RETRY_FIRST_DELAY_MS, SAVE_RETRY_MAX_DELAY_MS } from './appAssistantConstants'

/** One bubble in the chat. `text` is already rendered in the user's language. */
export type AssistantChatMessage = {
  id: string
  role: AssistantSpeakerRole
  text: string
  action?: AppAssistantAction
  quickActions?: AppAssistantQuickAction[]
  /**
   * A human from the Intraction team answered this, not Aria. Rendered distinctly, because a user must
   * never mistake a person's answer for the assistant's, or the other way round.
   */
  fromSupport?: boolean
  /** Part of a support request, in either direction: it stays with the request, never in Aria's memory. */
  isSupportThread?: boolean
  /** Worded by the app rather than by them or Aria — the opening card, a pressed chip, a fixed reply — so not kept. */
  isAppNotice?: boolean
}

export type AssistantSubmitOptions = {
  message?: string
  quickActionId?: AppAssistantQuickActionId
  targetId?: string
}

/**
 * The backend answers with translation keys (deterministic replies) or with model prose already written
 * in the user's language. Both become a plain string here, at the only place that has `t`.
 */
export function resolveAssistantMessage(t: TFunction, message: AssistantMessage): string {
  if (message.source === AssistantMessageSource.MODEL) return message.text
  return t(message.key, message.params)
}

export function resolveAssistantLabel(t: TFunction, label: AssistantLabel): string {
  return t(label.key, label.params)
}

/**
 * A typed-chat reply flattened to one spoken string.
 *
 * A chat answer can be prose, or a short line plus a button ("Opening your feedback"). Spoken, there is
 * no button — so the model gets the prose it should read out, with the destination named in words.
 */
export function resolveAssistantAnswerText(t: TFunction, result: AppAssistantResult): string {
  const message = resolveAssistantMessage(t, result.message)
  if (result.action.type !== AppAssistantActionType.ANSWER) return message

  return [message, result.action.text].filter(Boolean).join(' ')
}

export function resolveQuickActionDescription(t: TFunction, quickAction: AppAssistantQuickAction): string | undefined {
  if (quickAction.descriptionKey) return t(quickAction.descriptionKey)
  // Real-world data (a team name) is shown as-is.
  return quickAction.descriptionText
}

/** The greeting bubble. Copy comes from the same catalog the backend uses, so it is translated too. */
export function getStarterMessages(t: TFunction, pathname: string): AssistantChatMessage[] {
  const isFacilitator = isFacilitatorPathname(pathname)
  const welcomeKey = isFacilitator
    ? AssistantMessageKey.CAPABILITIES_FACILITATOR
    : AssistantMessageKey.CAPABILITIES_GAMER

  return [
    {
      id: 'welcome',
      role: AssistantSpeakerRole.ASSISTANT,
      text: t(welcomeKey),
      quickActions: getAssistantQuickActions(isFacilitator),
      isAppNotice: true
    }
  ]
}

const ACTIONS_WITH_URL = [
  AppAssistantActionType.NAVIGATE,
  AppAssistantActionType.SHOW_SPRINT,
  AppAssistantActionType.SHOW_FEEDBACK
]

const hasUrl = (action: AppAssistantAction): boolean => ACTIONS_WITH_URL.includes(action.type)

export function getActionUrl(action?: AppAssistantAction): string | undefined {
  if (!action || !hasUrl(action)) return undefined
  return 'url' in action ? action.url : undefined
}

/** Label on the action button, translated. '' when the action has no button. */
export function resolveActionLabel(t: TFunction, action: AppAssistantAction): string {
  if (action.type === AppAssistantActionType.NAVIGATE || action.type === AppAssistantActionType.SHOW_FEEDBACK) {
    return resolveAssistantLabel(t, action.label)
  }
  if (action.type === AppAssistantActionType.SHOW_SPRINT) {
    return t(AssistantActionLabelKey.OPEN_SPRINTS)
  }
  return ''
}

export function getActionRouteState(action?: AppAssistantAction): Record<string, unknown> | undefined {
  if (!action || !hasUrl(action)) return undefined
  return 'routeState' in action ? action.routeState : undefined
}

// A targeted quick action must carry its target id, an untargeted one must not — the request type
// enforces the pairing, so the branch lives here instead of being assembled field by field.
export function buildQuickActionRequest(
  quickActionId: AppAssistantQuickActionId,
  targetId: string | undefined,
  pathname: string
): AppAssistantRequest {
  const context = { pathname }
  return isTargetedQuickActionId(quickActionId)
    ? { quickActionId, targetId: targetId ?? '', context }
    : { quickActionId, context }
}

/** Carries the open conversation across the one reload Aria asks for, so a long talk is not started over. */
const CONVERSATION_BEFORE_RELOAD_KEY = 'intraction_assistant_conversation_before_reload'

/** Enough to keep the thread of a long talk without filling session storage with one. */
const MAX_CARRIED_MESSAGES = 40

/** The conversation carried over the reload, and which of its messages had already been saved for her memory. */
type StashedConversation = { messages: AssistantChatMessage[]; savedMessageIds: string[] }

export function stashAssistantConversation(messages: AssistantChatMessage[], savedMessageIds: string[]): void {
  const stash: StashedConversation = { messages: messages.slice(-MAX_CARRIED_MESSAGES), savedMessageIds }

  try {
    sessionStorage.setItem(CONVERSATION_BEFORE_RELOAD_KEY, JSON.stringify(stash))
  } catch {
    // Losing it only costs the carry-over; the reload Aria asked for still has to happen.
  }
}

function peekStash(): StashedConversation | undefined {
  try {
    const stored = sessionStorage.getItem(CONVERSATION_BEFORE_RELOAD_KEY)
    const parsed = stored ? (JSON.parse(stored) as Partial<StashedConversation>) : undefined
    const isWholeStash = Array.isArray(parsed?.messages) && Array.isArray(parsed?.savedMessageIds)
    return isWholeStash ? (parsed as StashedConversation) : undefined
  } catch {
    return undefined
  }
}

/** Read without clearing: the panel reads this while rendering, and a render that never commits must not eat it. */
export function peekStashedAssistantConversation(): AssistantChatMessage[] | undefined {
  const messages = peekStash()?.messages
  const hasCarriedMessages = !!messages && messages.length > 0

  return hasCarriedMessages ? messages : undefined
}

/** Which of the carried messages were saved before the reload, so the rest are saved after it and none twice. */
export const peekStashedSavedMessageIds = (): string[] => peekStash()?.savedMessageIds ?? []

/** Cleared once the panel has mounted, so it can never resurface on a reload the user chose themselves. */
export function clearStashedAssistantConversation(): void {
  try {
    sessionStorage.removeItem(CONVERSATION_BEFORE_RELOAD_KEY)
  } catch {
    // Nothing to do: a stash that cannot be cleared is dropped on the next read anyway.
  }
}

/** A spoken turn as a chat message: the session it came from and the turn, since turn ids restart with each one. */
export const buildSpokenMessageId = (sessionKey: number, turnId: string): string => `spoken-${sessionKey}-${turnId}`

/** The chat plus what a live spoken session holds now, which reaches the chat only when it stops; nothing doubles. */
export function mergeLiveTurnsIntoMessages(
  messages: AssistantChatMessage[],
  liveTurns: AriaTranscriptTurn[],
  sessionKey: number
): AssistantChatMessage[] {
  const spoken = meaningfulAriaTranscriptTurns(liveTurns)
  if (spoken.length === 0) return messages

  const existingIds = new Set(messages.map((message) => message.id))

  const added = spoken
    .map((turn) => ({ id: buildSpokenMessageId(sessionKey, turn.id), role: turn.role, text: turn.text.trim() }))
    .filter((message) => !existingIds.has(message.id))

  return added.length > 0 ? [...messages, ...added] : messages
}

/** Said by them or by Aria in the panel — not the app's own notices, and not a support request. */
export function isRememberedMessage(message: AssistantChatMessage): boolean {
  const isConversationWithAria = !message.isAppNotice && !message.isSupportThread
  return isConversationWithAria && message.text.trim().length > 0
}

/** Everything said in the panel and not saved yet, the finished turns of a live spoken session included. */
export function selectUnsavedMessages(params: {
  messages: AssistantChatMessage[]
  liveTurns: AriaTranscriptTurn[]
  sessionKey: number
  savedIds: Set<string>
}): AssistantChatMessage[] {
  const { messages, liveTurns, sessionKey, savedIds } = params
  const finishedTurns = liveTurns.filter((turn) => turn.isComplete)
  const conversation = mergeLiveTurnsIntoMessages(messages, finishedTurns, sessionKey)

  return conversation.filter((message) => isRememberedMessage(message) && !savedIds.has(message.id))
}

/** How long to wait before trying a failed save again: longer with each failure in a row, up to a ceiling. */
export function resolveSaveRetryDelayMs(failuresInARow: number): number {
  return Math.min(
    SAVE_RETRY_FIRST_DELAY_MS * SAVE_RETRY_BACKOFF_FACTOR ** (failuresInARow - 1),
    SAVE_RETRY_MAX_DELAY_MS
  )
}

/** How much of the talk is quoted back to the model: after a reload, or before a typed message. */
const CONVERSATION_TAIL_MESSAGE_COUNT = 6

/**
 * The last turns of the talk with Aria as one plain line, for her to read back. The app's own notices and a support
 * request are not her talk, and a colleague's reply must never read as something she said.
 */
export function buildAssistantConversationTail(messages: AssistantChatMessage[]): string {
  return messages
    .filter(isRememberedMessage)
    .slice(-CONVERSATION_TAIL_MESSAGE_COUNT)
    .map((message) => `${ASSISTANT_TAIL_SPEAKER_LABELS[message.role]}: ${message.text.trim()}`)
    .join(' ')
}
